import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Modal, ModalHeader, ModalBody, Table,
  Spinner, Alert, Button, Row, Col,
} from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileExcel, faFilePdf } from '@fortawesome/free-solid-svg-icons';
import { get } from '../../../helpers/requests';
import { cellDateOperation } from '../../../helpers/tables';
import { FRONT_MF, BASE_URL, API } from '../../../CONST';
import { fileDownload } from '../../../helpers/helpers';
import { useSelector } from 'react-redux';
import 'styles/modal.css';

const fmt = (val) =>
  val != null ? `$${Math.round(val).toLocaleString('es-CL')}` : '—';

const meses = [
  'Enero','Febrero','Marzo','Abril','Mayo','Junio',
  'Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre',
];

const periodoLabel = (periodoStr) => {
  if (!periodoStr) return '—';
  const d = new Date(periodoStr);
  return `${meses[d.getMonth()]} ${d.getFullYear()}`;
};

const CorredorLiquidacionModal = ({ liquidacion, onClose, corredor }) => {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const loginIdToken = useSelector((state) => state.login.id_token);

  useEffect(() => {
    if (!liquidacion) return;

    setLoading(true);
    setError(null);
    setDetail(null);

    const url = `${BASE_URL}${API.liquidacion}/${liquidacion.id}/`;
    get(corredor ? `${url}?corredor_id=${corredor.id}` : url)
      .then((resp) => setDetail(resp))
      .catch((err) => setError(err.message || 'Error al cargar el detalle.'))
      .finally(() => setLoading(false));
  }, [liquidacion?.id]);

  const pagos = detail?.pagos_credito || [];
  const totalPrimaNeta = pagos.reduce((s, p) => s + (p.prima_neta || 0), 0);
  const totalComision = pagos.reduce((s, p) => s + (p.comision_neta || 0), 0);

  const downloadUrl = corredor
    ? `${BASE_URL}${API.liquidacion}/${liquidacion?.id}/download_detail_comisiones_xlsx/?corredor_id=${corredor.id}`
    : `${BASE_URL}${API.liquidacion}/${liquidacion?.id}/download_detail_comisiones_xlsx/`;

  const downloadUrlPdf = corredor
    ? `${BASE_URL}${API.liquidacion}/${liquidacion?.id}/download_detail_comisiones_pdf/?corredor_id=${corredor.id}`
    : `${BASE_URL}${API.liquidacion}/${liquidacion?.id}/download_detail_comisiones_pdf/`;


  return (
    <Modal isOpen={!!liquidacion} toggle={onClose} size="xl" className="liquidacion-modal liquidacion-modal--wide">
      <ModalHeader toggle={onClose}>
        Detalle de Liquidación
      </ModalHeader>

      <ModalBody className="liquidacion-modal__body">
        {loading && (
          <div className="liquidacion-detail__loading">
            <Spinner color="primary" />
            <span>Cargando detalle...</span>
          </div>
        )}

        {error && !loading && <Alert color="danger">{error}</Alert>}

        {!loading && !error && detail && (
          <>
            <div className="liquidacion-detail__product-label">PRODUCTO: CRÉDITO</div>
            <div className="liquidacion-detail__meta">
              <strong>Período:</strong> {periodoLabel(detail.periodo)}
            </div>
            <div className="liquidacion-detail__meta" style={{ marginBottom: 12 }}>
              <strong>Folio:</strong> {detail.folio_liquidacion || '—'}
            </div>
            <div className="liquidacion-detail__title">
              INFORME DE LIQUIDACIÓN N° {detail.folio_liquidacion} — Período {periodoLabel(detail.periodo)}
            </div>

            <Row className="mb-3">
              <Col md="6">
                <table className="liquidacion-detail__info-table">
                  <tbody>
                    <tr>
                      <td>RUT Intermediario</td>
                      <td><strong>{detail.intermediario?.rut || '—'}</strong></td>
                    </tr>
                    <tr>
                      <td>Nombre Intermediario</td>
                      <td><strong>{detail.intermediario?.nombre || '—'}</strong></td>
                    </tr>
                  </tbody>
                </table>
              </Col>
              <Col md="6">
                <table className="liquidacion-detail__info-table liquidacion-detail__summary">
                  <tbody>
                    <tr>
                      <td>Total Prima Neta Recaudada CLP</td>
                      <td><strong>{fmt(totalPrimaNeta)}</strong></td>
                    </tr>
                    <tr>
                      <td>Comisión Generada</td>
                      <td><strong>{fmt(totalComision)}</strong></td>
                    </tr>
                  </tbody>
                </table>
              </Col>
            </Row>

            <hr />

            <div className="liquidacion-detail__download">
              <Button color="success" size="sm" onClick={() => fileDownload(downloadUrl, loginIdToken)}>
                <FontAwesomeIcon icon={faFileExcel} className="mr-2" />
                Descargar Excel
              </Button>
              <Button color="danger" size="sm" onClick={() => fileDownload(downloadUrlPdf, loginIdToken)} className="ml-2">
                <FontAwesomeIcon icon={faFilePdf} className="mr-2" />
                Descargar Pdf
              </Button>
            </div>

            {pagos.length === 0 ? (
              <Alert color="secondary">Sin pagos registrados para esta liquidación.</Alert>
            ) : (
              <Table hover responsive bordered size="sm" className="liquidacion-modal__table">
                <thead>
                  <tr>
                    <th>N° Póliza</th>
                    <th>Factura</th>
                    <th>RUT Contratante</th>
                    <th>Contratante</th>
                    <th>Período Pago</th>
                    <th className="text-right">Prima Neta CLP</th>
                    <th className="text-right">IVA CLP</th>
                    <th className="text-right">Prima Bruta CLP</th>
                    <th className="text-right">% Comisión</th>
                    <th className="text-right">Comisión CLP</th>
                    <th>Plan de Pago</th>
                    <th>Observaciones</th>
                  </tr>
                </thead>
                <tbody>
                  {pagos.map((pago, idx) => (
                    <tr key={pago.id || idx}>
                      <td>{pago.poliza?.nemotecnico ?? '—'}</td>
                      <td>{pago.factura_numero ?? '—'}</td>
                      <td>{pago.contratante?.rut ?? '—'}</td>
                      <td>{pago.contratante?.nombre ?? '—'}</td>
                      <td className="text-center">{periodoLabel(detail.periodo) ?? '—'}</td>
                      <td className="text-right">{fmt(pago.prima_neta)}</td>
                      <td className="text-right">{fmt(pago.iva)}</td>
                      <td className="text-right">{fmt(pago.prima_bruta)}</td>
                      <td className="text-right">{pago.comision_pct != null ? `${pago.comision_pct}%` : '—'}</td>
                      <td className="text-right">{fmt(pago.comision_neta)}</td>
                      <td>{pago.plan_pago_cuotas ?? '—'}</td>
                      <td>{pago.observaciones ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </>
        )}
      </ModalBody>
    </Modal>
  );
};

CorredorLiquidacionModal.propTypes = {
  liquidacion: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  corredor: PropTypes.object,
};

export default CorredorLiquidacionModal;